/** Matches Supabase "Lowercase, uppercase letters, digits and symbols (recommended)" policy. */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 72;

/** Allowed symbols per Supabase Auth docs */
export const PASSWORD_SYMBOLS = "!@#$%^&*()_+-=[]{};':\"|<>?,./`~";

const LOWERCASE = /[a-z]/;
const UPPERCASE = /[A-Z]/;
const DIGIT = /[0-9]/;
const SYMBOL = new RegExp(
  `[${PASSWORD_SYMBOLS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}]`,
);

export type PasswordRequirementId =
  | "length"
  | "lowercase"
  | "uppercase"
  | "digit"
  | "symbol";

export interface PasswordRequirement {
  id: PasswordRequirementId;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "lowercase",
    label: "One lowercase letter (a–z)",
    test: (p) => LOWERCASE.test(p),
  },
  {
    id: "uppercase",
    label: "One uppercase letter (A–Z)",
    test: (p) => UPPERCASE.test(p),
  },
  {
    id: "digit",
    label: "One number (0–9)",
    test: (p) => DIGIT.test(p),
  },
  {
    id: "symbol",
    label: "One symbol (!@#$%…)",
    test: (p) => SYMBOL.test(p),
  },
];

export type PasswordStrengthLabel =
  | "Very weak"
  | "Weak"
  | "Fair"
  | "Good"
  | "Strong";

export interface PasswordStrength {
  score: number;
  label: PasswordStrengthLabel;
  color: string;
  metCount: number;
  totalCount: number;
}

export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return {
      score: 0,
      label: "Very weak",
      color: "red.400",
      metCount: 0,
      totalCount: PASSWORD_REQUIREMENTS.length,
    };
  }

  const metCount = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;
  const totalCount = PASSWORD_REQUIREMENTS.length;
  const score = Math.round((metCount / totalCount) * 100);

  let label: PasswordStrengthLabel;
  let color: string;

  if (score === 100) {
    label = "Strong";
    color = "green.500";
  } else if (score >= 80) {
    label = "Good";
    color = "teal.500";
  } else if (score >= 60) {
    label = "Fair";
    color = "yellow.500";
  } else if (score >= 40) {
    label = "Weak";
    color = "orange.400";
  } else {
    label = "Very weak";
    color = "red.400";
  }

  return { score, label, color, metCount, totalCount };
}

export function validatePassword(password: string): {
  valid: boolean;
  message?: string;
  failedRequirement?: PasswordRequirementId;
} {
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      valid: false,
      message: `Password cannot be longer than ${PASSWORD_MAX_LENGTH} characters`,
      failedRequirement: "length",
    };
  }

  for (const req of PASSWORD_REQUIREMENTS) {
    if (!req.test(password)) {
      return {
        valid: false,
        message: `Password must meet all requirements. Missing: ${req.label.toLowerCase()}.`,
        failedRequirement: req.id,
      };
    }
  }

  return { valid: true };
}

export function mapSupabasePasswordError(error: unknown): string {
  const err = error as {
    message?: string;
    error_description?: string;
    weak_password?: { reasons?: string[]; message?: string };
  };

  const weak = err?.weak_password;
  if (weak?.reasons?.length) {
    const parts: string[] = [];
    if (weak.reasons.includes("length")) {
      parts.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
    }
    if (weak.reasons.includes("characters")) {
      parts.push(
        "Password must include lowercase and uppercase letters, a number, and a symbol.",
      );
    }
    if (weak.reasons.includes("pwned")) {
      parts.push(
        "This password is known to be weak or compromised. Please choose a different one.",
      );
    }
    if (parts.length) return parts.join(" ");
  }

  if (weak?.message) return weak.message;

  const message = err?.error_description || err?.message || "";
  if (/password/i.test(message)) return message;

  return "Password does not meet security requirements. Please check the requirements below.";
}
